import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { coffeeInventoryService } from '../services/coffeeInventoryService';
import { partnerDebtDraftService } from '../services/partnerDebtDraftService';
import { warehouseGoodsCatalogService } from '../services/warehouseGoodsCatalogService';
import {
  CoffeePartnerContractItem,
  CoffeePartner,
  PartnerDebtDraft,
  PartnerDebtDraftLine,
} from '../types/coffeeInventory';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { downloadPartnerDebtInvoicePdf } from '../utils/partnerDebtInvoicePdf';
import { formatInputNumberFormatter, parseFlexibleNumber } from '../utils/numberInput';

interface PartnerFormValues {
  name: string;
  defaultDiscountRate: number;
  contractItems?: Array<{
    coffeeTypeId: string;
    unitPrice: number;
  }>;
  notes?: string;
}

interface DebtDraftFormValues {
  partnerId: string;
  /** Ngày nhập phiếu (ghi trên phiếu công nợ) */
  issueDate: Dayjs;
  discountRate: number;
  notes?: string;
  lines: Array<{
    coffeeTypeId?: string;
    quantity: number;
    unitPrice: number;
  }>;
}

interface FinalizeDraftFormValues {
  date: Dayjs;
}

const normalizeText = (value?: string) => (value || '').trim().toLowerCase();

const normalizeDiscountRate = (value?: number) => Math.max(0, Math.min(Number(value || 0), 100));

const calculateDiscountedAmount = (amount: number, discountRate?: number) => {
  const normalizedRate = normalizeDiscountRate(discountRate);
  return Number(amount || 0) * (1 - normalizedRate / 100);
};

/** Phiếu công nợ: form nhập kg, Firestore/kho lưu gram. */
const GRAMS_PER_KG = 1000;
const debtDraftKgToGrams = (kg: number) => Math.round(Number(kg) * GRAMS_PER_KG);
const debtDraftGramsToKg = (grams: number) => Number(grams || 0) / GRAMS_PER_KG;

export interface PartnerCoffeeManagementSectionProps {
  /** Mỗi lần tăng (vd. khi chọn lại tab Đối tác), tải lại đối tác + phiếu công nợ. */
  reloadToken?: number;
}

const PartnerCoffeeManagementSection: React.FC<PartnerCoffeeManagementSectionProps> = ({
  reloadToken = 0,
}) => {
  const [partnerForm] = Form.useForm<PartnerFormValues>();
  const [debtDraftForm] = Form.useForm<DebtDraftFormValues>();
  const [finalizeForm] = Form.useForm<FinalizeDraftFormValues>();

  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<CoffeePartner[]>([]);
  const [partnerDebtDrafts, setPartnerDebtDrafts] = useState<PartnerDebtDraft[]>([]);
  const [warehouseCatalogGoods, setWarehouseCatalogGoods] = useState<WarehouseGoodsItem[]>([]);

  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [debtDraftModalOpen, setDebtDraftModalOpen] = useState(false);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);

  const [editingPartner, setEditingPartner] = useState<CoffeePartner | null>(null);
  const [editingDebtDraft, setEditingDebtDraft] = useState<PartnerDebtDraft | null>(null);
  const [draftToFinalize, setDraftToFinalize] = useState<PartnerDebtDraft | null>(null);
  const [finalizeSubmitting, setFinalizeSubmitting] = useState(false);
  /** Lọc bảng phiếu chờ xuất HĐ theo đối tác (undefined = tất cả). */
  const [debtDraftPartnerFilter, setDebtDraftPartnerFilter] = useState<string | undefined>(undefined);

  const watchedContractItems = Form.useWatch('contractItems', partnerForm) || [];
  const watchedDebtDraftLines = Form.useWatch('lines', debtDraftForm) || [];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [partnerRows, draftRows, catalogRows] = await Promise.all([
        coffeeInventoryService.getAllPartners(),
        partnerDebtDraftService.getAllDrafts(),
        warehouseGoodsCatalogService.getAll(),
      ]);

      setPartners(partnerRows || []);
      setPartnerDebtDrafts(draftRows || []);
      setWarehouseCatalogGoods(catalogRows || []);
    } catch (error) {
      message.error('Không thể tải dữ liệu quản lý cà phê đối tác.');
      setPartners([]);
      setPartnerDebtDrafts([]);
      setWarehouseCatalogGoods([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, reloadToken]);

  const resolveWarehouseCatalogGood = (raw?: string): WarehouseGoodsItem | undefined => {
    const s = String(raw || '').trim();
    if (!s) return undefined;
    const byId = warehouseCatalogGoods.find((g) => g.id === s);
    if (byId) return byId;
    const byNormId = warehouseCatalogGoods.find((g) => !!(g.id && normalizeText(g.id) === normalizeText(s)));
    if (byNormId) return byNormId;
    const byCode = warehouseCatalogGoods.find(
      (g) => g.productCode && normalizeText(g.productCode) === normalizeText(s)
    );
    if (byCode) return byCode;
    return warehouseCatalogGoods.find((g) => normalizeText(g.name) === normalizeText(s));
  };

  /** Luôn dùng id Firestore làm value Select để hiển thị đúng tên hàng (tránh hiện mã khi lệch value). */
  const warehouseGoodIdForSelect = (raw?: string) =>
    resolveWarehouseCatalogGood(raw)?.id || String(raw || '').trim();

  /** Tìm theo tên, mã sản phẩm hoặc id (value luôn là id Firestore → Select hiển thị label = tên). */
  const warehouseGoodsSelectFilterOption = useCallback(
    (input: string, option?: { value?: string }) => {
      const q = normalizeText(input);
      if (!q) return true;
      const id = String(option?.value ?? '');
      const g = warehouseCatalogGoods.find((x) => x.id === id);
      if (!g) return false;
      const hay = normalizeText(`${g.name} ${g.productCode} ${g.id}`);
      return hay.includes(q);
    },
    [warehouseCatalogGoods]
  );

  /** Danh mục hàng hóa kho tổng — hợp đồng đối tác & phiếu công nợ (đồng bộ danh mục). */
  const debtDraftWarehouseGoodsOptions = useMemo(() => {
    return warehouseCatalogGoods
      .filter((g) => !!g.id)
      .map((g) => {
        const name = String(g.name || '').trim();
        const code = String(g.productCode || '').trim();
        const id = g.id as string;
        const label = name ? (code ? `${name} (${code})` : name) : code || id;
        return {
          value: id,
          label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  }, [warehouseCatalogGoods]);

  const partnerMap = useMemo(() => {
    return partners.reduce((acc, item) => {
      if (item.id) acc[item.id] = item;
      return acc;
    }, {} as Record<string, CoffeePartner>);
  }, [partners]);

  const openCreatePartnerModal = () => {
    setEditingPartner(null);
    partnerForm.resetFields();
    partnerForm.setFieldsValue({ defaultDiscountRate: 0, contractItems: [] });
    setPartnerModalOpen(true);
  };

  const openEditPartnerModal = (record: CoffeePartner) => {
    setEditingPartner(record);
    partnerForm.setFieldsValue({
      name: record.name,
      defaultDiscountRate: Number(record.defaultDiscountRate || 0),
      contractItems: (record.contractItems || []).map((item) => ({
        coffeeTypeId: warehouseGoodIdForSelect(item.coffeeTypeId),
        unitPrice: Number(item.unitPrice || 0),
      })),
      notes: record.notes || '',
    });
    setPartnerModalOpen(true);
  };

  const handleSavePartner = async () => {
    try {
      const values = await partnerForm.validateFields();
      const rawContractItems = values.contractItems || [];
      const contractGoodIds = rawContractItems
        .map((item) => String(item?.coffeeTypeId || '').trim())
        .filter((id) => !!id);

      if (!contractGoodIds.length) {
        message.error('Vui lòng nhập ít nhất 1 mặt hàng trong hợp đồng.');
        return;
      }

      if (new Set(contractGoodIds).size !== contractGoodIds.length) {
        message.error('Hợp đồng đang bị trùng mặt hàng. Vui lòng kiểm tra lại.');
        return;
      }

      const contractItems: CoffeePartnerContractItem[] = rawContractItems
        .map((item) => {
          const good = resolveWarehouseCatalogGood(item?.coffeeTypeId);
          if (!good?.id) return null;
          const unitPrice = Number(item.unitPrice || 0);
          if (unitPrice <= 0) return null;
          return {
            coffeeTypeId: good.id,
            coffeeTypeName: String(good.name || '').trim(),
            unitPrice,
          };
        })
        .filter((item): item is CoffeePartnerContractItem => !!item);

      if (!contractItems.length) {
        message.error('Hợp đồng cần ít nhất một mặt hàng hợp lệ từ danh mục kho và đơn giá/kg > 0.');
        return;
      }

      const payload = {
        name: values.name.trim(),
        defaultDiscountRate: Number(values.defaultDiscountRate || 0),
        contractItems,
        notes: (values.notes || '').trim(),
      };

      if (editingPartner?.id) {
        await coffeeInventoryService.updatePartner(editingPartner.id, payload);
        message.success('Đã cập nhật đối tác.');
      } else {
        await coffeeInventoryService.addPartner(payload);
        message.success('Đã tạo đối tác mới.');
      }

      setPartnerModalOpen(false);
      setEditingPartner(null);
      partnerForm.resetFields();
      loadData();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error('Không thể lưu đối tác.');
    }
  };

  const handleDeletePartner = async (id?: string) => {
    if (!id) return;
    try {
      await coffeeInventoryService.deletePartner(id);
      message.success('Đã xóa đối tác.');
      loadData();
    } catch (error) {
      message.error('Không thể xóa đối tác.');
    }
  };

  const handleDebtDraftPartnerChange = (partnerId: string) => {
    const selectedPartner = partnerMap[partnerId];
    if (!selectedPartner) return;
    const contract = selectedPartner.contractItems || [];
    const linesFromContract =
      contract.length > 0
        ? contract.map((item) => ({
            coffeeTypeId: warehouseGoodIdForSelect(item.coffeeTypeId),
            quantity: 0.1,
            unitPrice: Number(item.unitPrice || 0),
          }))
        : [{ quantity: 0.1, unitPrice: 0 }];
    const patch: Record<string, unknown> = {
      discountRate: Number(selectedPartner.defaultDiscountRate || 0),
    };
    if (!editingDebtDraft) {
      patch.lines = linesFromContract;
    }
    debtDraftForm.setFieldsValue(patch);
  };

  const openCreateDebtDraftModal = () => {
    setEditingDebtDraft(null);
    debtDraftForm.resetFields();
    debtDraftForm.setFieldsValue({
      issueDate: dayjs(),
      discountRate: 0,
      lines: [{ quantity: 0.1, unitPrice: 0 }],
    });
    setDebtDraftModalOpen(true);
  };

  const openEditDebtDraftModal = (record: PartnerDebtDraft) => {
    setEditingDebtDraft(record);
    debtDraftForm.resetFields();
    debtDraftForm.setFieldsValue({
      partnerId: record.partnerId,
      issueDate: dayjs(record.issueDate || record.createdAt || new Date()),
      discountRate: Number(record.discountRate || 0),
      notes: record.notes || '',
      lines: (record.lines || []).map((l) => ({
        coffeeTypeId: warehouseGoodIdForSelect(l.coffeeTypeId),
        quantity: debtDraftGramsToKg(Number(l.quantity || 0)),
        unitPrice: Number(l.unitPricePerKg || 0),
      })),
    });
    setDebtDraftModalOpen(true);
  };

  const handleSaveDebtDraft = async () => {
    try {
      const values = await debtDraftForm.validateFields();
      const selectedPartner = partnerMap[values.partnerId];
      if (!selectedPartner?.id) {
        message.error('Vui lòng chọn đối tác hợp lệ.');
        return;
      }
      const rawLines = values.lines || [];

      const lines = rawLines
        .map((item) => {
          const good = resolveWarehouseCatalogGood(item?.coffeeTypeId);
          if (!good?.id || item?.quantity == null || item?.unitPrice == null) return null;
          if (Number(item.quantity) <= 0 || Number(item.unitPrice) <= 0) return null;
          return {
            coffeeTypeId: good.id,
            coffeeTypeName: String(good.name || '').trim(),
            quantity: debtDraftKgToGrams(Number(item.quantity)),
            unitPricePerKg: Number(item.unitPrice),
          };
        })
        .filter((item): item is PartnerDebtDraftLine => !!item);

      if (!lines.length) {
        message.error('Cần ít nhất một dòng hàng hóa (danh mục kho) với số lượng và đơn giá hợp lệ.');
        return;
      }

      const keys = lines.map((l) => l.coffeeTypeId);
      if (new Set(keys).size !== keys.length) {
        message.error('Trùng mặt hàng trong phiếu. Gộp thành một dòng hoặc xóa trùng.');
        return;
      }

      const payloadBase = {
        partnerId: selectedPartner.id as string,
        partnerName: selectedPartner.name,
        issueDate: values.issueDate.toDate(),
        discountRate: normalizeDiscountRate(values.discountRate),
        lines,
        notes: (values.notes || '').trim(),
      };

      if (editingDebtDraft?.id) {
        await partnerDebtDraftService.updateDraft(editingDebtDraft.id, payloadBase);
        message.success('Đã cập nhật phiếu công nợ.');
      } else {
        await partnerDebtDraftService.addDraft(payloadBase);
        message.success('Đã lưu phiếu công nợ.');
      }

      setDebtDraftModalOpen(false);
      setEditingDebtDraft(null);
      debtDraftForm.resetFields();
      loadData();
    } catch (error: any) {
      if (error?.errorFields) return Promise.reject(error);
      message.error('Không thể lưu phiếu công nợ.');
      return Promise.reject(error);
    }
  };

  const handleDeleteDebtDraft = async (draft: PartnerDebtDraft) => {
    const id = draft.id;
    if (!id) return;
    try {
      await partnerDebtDraftService.deleteDraft(id);
      message.success(
        draft.status === 'finalized'
          ? 'Đã xóa phiếu, gỡ ghi nhận xuất đối tác và hoàn trả tồn kho tổng.'
          : 'Đã xóa phiếu nháp (chưa có ghi nhận kho).'
      );
      loadData();
    } catch (error: any) {
      message.error(String(error?.message || 'Không thể xóa phiếu.'));
    }
  };

  const openFinalizeDebtDraftModal = (record: PartnerDebtDraft) => {
    setDraftToFinalize(record);
    finalizeForm.resetFields();
    finalizeForm.setFieldsValue({
      date: dayjs(record.issueDate || record.createdAt || new Date()),
    });
    setFinalizeModalOpen(true);
  };

  const handleConfirmFinalizeDebtDraft = async () => {
    if (!draftToFinalize?.id) return Promise.reject(new Error('Thiếu phiếu'));
    try {
      const values = await finalizeForm.validateFields();
      setFinalizeSubmitting(true);
      const storeLabel = 'Kho tổng';
      const invoiceDate = values.date.toDate();

      await partnerDebtDraftService.finalizeDraft(draftToFinalize.id, {
        date: invoiceDate,
        storeName: storeLabel,
      });

      const mergedDraft: PartnerDebtDraft = {
        ...draftToFinalize,
        status: 'finalized',
        finalizedInvoiceDate: invoiceDate,
        finalizedStoreName: storeLabel,
      };
      try {
        await downloadPartnerDebtInvoicePdf({
          draft: mergedDraft,
          invoiceDate,
          storeDisplayName: storeLabel,
        });
        message.success('Đã xuất HĐ, ghi nhận xuất kho và tải file PDF.');
      } catch (pdfErr) {
        console.error(pdfErr);
        message.warning('Đã chốt phiếu và ghi kho nhưng tạo PDF thất bại. Dùng nút Tải PDF trên dòng phiếu.');
      }

      setFinalizeModalOpen(false);
      setDraftToFinalize(null);
      finalizeForm.resetFields();
      await loadData();
    } catch (error: any) {
      if (error?.errorFields) return Promise.reject(error);
      message.error(String(error?.message || 'Không thể chốt phiếu.'));
      return Promise.reject(error);
    } finally {
      setFinalizeSubmitting(false);
    }
  };

  const handleDownloadDebtInvoicePdf = async (record: PartnerDebtDraft) => {
    try {
      const invoiceDate = record.finalizedInvoiceDate || record.finalizedAt || new Date();
      const storeDisplayName = record.finalizedStoreName || '—';
      await downloadPartnerDebtInvoicePdf({
        draft: record,
        invoiceDate,
        storeDisplayName,
      });
      message.success('Đã tải PDF.');
    } catch (e) {
      console.error(e);
      message.error('Không tạo được file PDF.');
    }
  };

  const partnerColumns = [
    {
      title: 'Tên đối tác',
      dataIndex: 'name',
      key: 'name',
      width: 240,
    },
    {
      title: '% chiết khấu mặc định',
      dataIndex: 'defaultDiscountRate',
      key: 'defaultDiscountRate',
      width: 180,
      align: 'right' as const,
      render: (value: number) => `${Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`,
    },
    {
      title: 'Số mặt hàng trong HĐ',
      key: 'contractItems',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, record: CoffeePartner) => Number(record.contractItems?.length || 0),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 220,
      render: (value: string) => value || '-',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 130,
      render: (_: unknown, record: CoffeePartner) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditPartnerModal(record)} />
          <Popconfirm
            title="Xóa đối tác"
            description="Bạn chắc chắn muốn xóa đối tác này?"
            onConfirm={() => handleDeletePartner(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const partnerDebtDraftTableRows = useMemo(() => {
    const filtered = debtDraftPartnerFilter
      ? partnerDebtDrafts.filter((d) => d.partnerId === debtDraftPartnerFilter)
      : partnerDebtDrafts;
    const rows = filtered.map((d) => {
      const lineTotal = d.lines.reduce(
        (sum, line) => sum + (Number(line.quantity || 0) / 1000) * Number(line.unitPricePerKg || 0),
        0
      );
      const afterDisc = calculateDiscountedAmount(lineTotal, d.discountRate);
      const issueRef = d.issueDate || d.createdAt;
      const issueTs = issueRef ? new Date(issueRef).getTime() : 0;
      return {
        key: d.id || `${d.partnerId}-${issueTs}`,
        draft: d,
        issueTs,
        issueDateLabel: issueRef ? dayjs(issueRef).format('DD/MM/YYYY') : '—',
        partnerName: d.partnerName,
        discountRate: d.discountRate,
        lineCount: d.lines.length,
        lineTotal,
        afterDisc,
      };
    });
    return rows.sort((a, b) => b.issueTs - a.issueTs);
  }, [partnerDebtDrafts, debtDraftPartnerFilter]);

  const debtDraftColumns = [
    {
      title: 'Ngày nhập phiếu',
      dataIndex: 'issueDateLabel',
      key: 'issueDate',
      width: 120,
      sorter: (a: { issueTs: number }, b: { issueTs: number }) => a.issueTs - b.issueTs,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Đối tác',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: 180,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_: unknown, row: { draft: PartnerDebtDraft }) =>
        row.draft.status === 'finalized' ? (
          <Tag color="success">Đã xuất HĐ</Tag>
        ) : (
          <Tag color="processing">Chờ xuất</Tag>
        ),
    },
    {
      title: '% CK',
      dataIndex: 'discountRate',
      key: 'discountRate',
      width: 90,
      align: 'right' as const,
      render: (value: number) => `${Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`,
    },
    {
      title: 'Số dòng',
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 90,
      align: 'right' as const,
    },
    {
      title: 'Tạm tính',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 130,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
    },
    {
      title: 'Sau CK',
      dataIndex: 'afterDisc',
      key: 'afterDisc',
      width: 130,
      align: 'right' as const,
      render: (value: number) => <b>{Number(value || 0).toLocaleString('vi-VN')}</b>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 320,
      fixed: 'right' as const,
      render: (_: unknown, row: { draft: PartnerDebtDraft }) => {
        const finalized = row.draft.status === 'finalized';
        return (
          <Space wrap>
            <Tooltip title={finalized ? 'Phiếu đã xuất HĐ, không chỉnh sửa' : undefined}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={finalized}
                onClick={() => openEditDebtDraftModal(row.draft)}
              />
            </Tooltip>
            {finalized ? (
              <Button
                type="primary"
                size="small"
                icon={<FilePdfOutlined />}
                onClick={() => handleDownloadDebtInvoicePdf(row.draft)}
              >
                Tải PDF
              </Button>
            ) : (
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                onClick={() => openFinalizeDebtDraftModal(row.draft)}
              >
                Xuất HĐ
              </Button>
            )}
            <Popconfirm
              title={finalized ? 'Xóa phiếu đã xuất HĐ?' : 'Xóa phiếu nháp?'}
              description={
                finalized ? (
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      Hệ thống sẽ <strong>gỡ toàn bộ ghi nhận xuất kho</strong> và giao dịch xuất đối tác gắn với phiếu
                      này, đồng thời <strong>hoàn trả số lượng đã trừ</strong> vào <strong>kho tổng</strong> (như hoàn
                      tác lệnh xuất).
                    </p>
                    <p style={{ marginBottom: 0, color: '#cf1322' }}>
                      File PDF đã tải trước đó vẫn có thể còn trên máy bạn — chỉ dữ liệu trên hệ thống được cập nhật.{' '}
                      <strong>Thao tác không thể hoàn tác.</strong>
                    </p>
                  </div>
                ) : (
                  <span>
                    Chỉ xóa phiếu <strong>chưa chốt</strong>. <strong>Chưa ảnh hưởng kho tổng</strong> vì chưa có xuất
                    HĐ.
                  </span>
                )
              }
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteDebtDraft(row.draft)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card
        title="Danh mục đối tác"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePartnerModal}>
            Thêm đối tác
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey={(record) => record.id || record.name}
          columns={partnerColumns}
          dataSource={partners}
          loading={loading}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 760 }}
          locale={{ emptyText: 'Chưa có đối tác nào.' }}
        />
      </Card>

      <Card
        title="Công nợ – phiếu chờ xuất HĐ"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDebtDraftModal}>
            Tạo phiếu công nợ
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <p style={{ marginBottom: 12, color: '#666' }}>
          Phiếu ghi <b>ngày nhập phiếu</b>; sau <b>Xuất HĐ</b> dòng vẫn nằm trong bảng với trạng thái <b>Đã xuất HĐ</b> và
          có nút <b>Tải PDF</b>. Khi chốt, hệ thống tải file PDF phiếu/hóa đơn về máy (nếu trình duyệt chặn popup, dùng
          Tải PDF). Hàng từ <b>danh mục kho tổng</b>.
        </p>
        <div style={{ marginBottom: 12, maxWidth: 320 }}>
          <Select
            allowClear
            placeholder="Lọc theo đối tác"
            style={{ width: '100%' }}
            value={debtDraftPartnerFilter}
            onChange={(v) => setDebtDraftPartnerFilter(v)}
            options={partners
              .filter((p) => !!p.id)
              .map((p) => ({ value: p.id as string, label: p.name }))}
          />
        </div>
        <Table
          rowKey="key"
          columns={debtDraftColumns}
          dataSource={partnerDebtDraftTableRows}
          loading={loading}
          pagination={{ pageSize: 6 }}
          scroll={{ x: 1180 }}
          locale={{ emptyText: 'Chưa có phiếu công nợ chờ xuất.' }}
        />
      </Card>

      <Modal
        title={editingPartner ? 'Sửa đối tác' : 'Thêm đối tác'}
        open={partnerModalOpen}
        onCancel={() => {
          setPartnerModalOpen(false);
          setEditingPartner(null);
          partnerForm.resetFields();
        }}
        onOk={handleSavePartner}
        okText="Lưu"
        cancelText="Hủy"
      >
        <Form form={partnerForm} layout="vertical">
          {warehouseCatalogGoods.length === 0 ? (
            <p style={{ color: '#fa8c16', marginBottom: 12 }}>
              Chưa có danh mục hàng hóa kho tổng. Thêm mục tại tab <b>Kho tổng</b> → <b>Danh mục hàng hóa</b>.
            </p>
          ) : null}
          <Form.Item
            name="name"
            label="Tên đối tác"
            rules={[{ required: true, message: 'Vui lòng nhập tên đối tác' }]}
          >
            <Input placeholder="Ví dụ: Đối tác A" />
          </Form.Item>
          <Form.Item
            name="defaultDiscountRate"
            label="% chiết khấu mặc định"
            rules={[{ required: true, message: 'Vui lòng nhập % chiết khấu' }]}
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={formatInputNumberFormatter(2)}
              parser={(value) => parseFlexibleNumber(value) as any}
            />
          </Form.Item>

          <Form.List
            name="contractItems"
            rules={[
              {
                validator: async (_, value) => {
                  if (!Array.isArray(value) || value.length === 0) {
                    return Promise.reject(new Error('Cần ít nhất 1 mặt hàng trong hợp đồng.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <Card size="small" title="Hợp đồng hàng hóa (kho tổng) & đơn giá/kg" style={{ marginBottom: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {fields.map((field) => (
                    <Row gutter={8} key={field.key}>
                      <Col span={14}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'coffeeTypeId']}
                          label="Hàng hóa (kho tổng)"
                          rules={[{ required: true, message: 'Chọn từ danh mục hàng hóa' }]}
                        >
                          <Select
                            showSearch
                            filterOption={warehouseGoodsSelectFilterOption}
                            placeholder="Chọn từ danh mục kho tổng"
                            notFoundContent="Không có mục trong danh mục."
                            options={debtDraftWarehouseGoodsOptions.map((opt) => ({
                              ...opt,
                              disabled: watchedContractItems.some(
                                (contractItem: any, contractIndex: number) =>
                                  contractIndex !== field.name &&
                                  String(contractItem?.coffeeTypeId || '').trim() === opt.value
                              ),
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'unitPrice']}
                          label="Đơn giá/kg"
                          rules={[
                            { required: true, message: 'Nhập đơn giá' },
                            {
                              validator: (_, value) =>
                                Number(value) > 0 ? Promise.resolve() : Promise.reject(new Error('Đơn giá phải > 0')),
                            },
                          ]}
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            formatter={formatInputNumberFormatter(2)}
                            parser={(value) => parseFlexibleNumber(value) as any}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                    Thêm mặt hàng vào hợp đồng
                  </Button>
                </Space>
              </Card>
            )}
          </Form.List>

          <Form.ErrorList errors={partnerForm.getFieldError('contractItems')} />

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingDebtDraft ? 'Sửa phiếu công nợ' : 'Tạo phiếu công nợ'}
        open={debtDraftModalOpen}
        onCancel={() => {
          setDebtDraftModalOpen(false);
          setEditingDebtDraft(null);
          debtDraftForm.resetFields();
        }}
        onOk={handleSaveDebtDraft}
        okText="Lưu phiếu"
        cancelText="Hủy"
        width={760}
      >
        <Form form={debtDraftForm} layout="vertical">
          {warehouseCatalogGoods.length === 0 ? (
            <p style={{ color: '#fa8c16', marginBottom: 12 }}>
              Chưa có danh mục hàng hóa kho tổng. Thêm mục tại tab <b>Kho tổng</b> → <b>Danh mục hàng hóa</b> để
              chọn nguyên liệu theo kho.
            </p>
          ) : null}
          <Form.Item
            name="partnerId"
            label="Đối tác"
            rules={[{ required: true, message: 'Vui lòng chọn đối tác' }]}
            extra={
              editingDebtDraft
                ? undefined
                : 'Khi tạo phiếu mới: chọn đối tác sẽ điền sẵn các dòng và đơn giá theo hợp đồng của đối tác (chỉnh SL kg trước khi lưu).'
            }
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn đối tác"
              onChange={handleDebtDraftPartnerChange}
              options={partners
                .filter((item) => !!item.id)
                .map((item) => ({
                  value: item.id as string,
                  label: `${item.name} (${Number(item.defaultDiscountRate || 0).toLocaleString('vi-VN')}%)`,
                }))}
            />
          </Form.Item>
          <Form.Item
            name="issueDate"
            label="Ngày nhập phiếu"
            rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="discountRate"
            label="% chiết khấu áp dụng cho cả phiếu"
            rules={[{ required: true, message: 'Vui lòng nhập % chiết khấu' }]}
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={formatInputNumberFormatter(2)}
              parser={(value) => parseFlexibleNumber(value) as any}
            />
          </Form.Item>

          <Form.List
            name="lines"
            rules={[
              {
                validator: async (_, value) => {
                  if (!Array.isArray(value) || value.length === 0) {
                    return Promise.reject(new Error('Cần ít nhất một dòng hàng hóa.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <Card size="small" title="Các dòng: danh mục kho – SL (kg) – đơn giá/kg" style={{ marginBottom: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {fields.map((field) => (
                    <Row gutter={8} key={field.key}>
                      <Col span={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'coffeeTypeId']}
                          label="Hàng hóa (kho tổng)"
                          rules={[{ required: true, message: 'Chọn từ danh mục hàng hóa' }]}
                        >
                          <Select
                            showSearch
                            filterOption={warehouseGoodsSelectFilterOption}
                            placeholder="Chọn từ danh mục hàng hóa (kho tổng)"
                            notFoundContent="Không có mục trong danh mục."
                            options={debtDraftWarehouseGoodsOptions.map((opt) => ({
                              ...opt,
                              disabled: watchedDebtDraftLines.some(
                                (row: any, rowIndex: number) =>
                                  rowIndex !== field.name &&
                                  String(row?.coffeeTypeId || '').trim() === opt.value
                              ),
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'quantity']}
                          label="SL (kg)"
                          extra="Khi lưu phiếu sẽ quy đổi sang gram."
                          rules={[
                            { required: true, message: 'Nhập số kg' },
                            {
                              validator: (_, value) =>
                                Number(value) > 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Số kg phải lớn hơn 0')),
                            },
                          ]}
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={0.001}
                            step={0.001}
                            precision={3}
                            formatter={formatInputNumberFormatter(3)}
                            parser={(value) => parseFlexibleNumber(value) as any}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'unitPrice']}
                          label="Đơn giá/kg"
                          rules={[
                            { required: true, message: 'Nhập đơn giá' },
                            {
                              validator: (_, value) =>
                                Number(value) > 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Đơn giá phải > 0')),
                            },
                          ]}
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            formatter={formatInputNumberFormatter(2)}
                            parser={(value) => parseFlexibleNumber(value) as any}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ display: 'flex', alignItems: 'end', paddingBottom: 8 }}>
                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ quantity: 0.1, unitPrice: 0 })}
                    block
                  >
                    Thêm dòng
                  </Button>
                </Space>
              </Card>
            )}
          </Form.List>

          <Form.ErrorList errors={debtDraftForm.getFieldError('lines')} />

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Xuất HĐ và ghi nhận xuất kho"
        open={finalizeModalOpen}
        onCancel={() => {
          setFinalizeModalOpen(false);
          setDraftToFinalize(null);
          finalizeForm.resetFields();
        }}
        onOk={handleConfirmFinalizeDebtDraft}
        okText="Chốt và ghi nhận"
        cancelText="Hủy"
        confirmLoading={finalizeSubmitting}
        destroyOnClose
      >
        {draftToFinalize ? (
          <p style={{ marginBottom: 12 }}>
            Ngày nhập phiếu:{' '}
            <b>
              {draftToFinalize.issueDate || draftToFinalize.createdAt
                ? dayjs(draftToFinalize.issueDate || draftToFinalize.createdAt).format('DD/MM/YYYY')
                : '—'}
            </b>
            <br />
            Đối tác: <b>{draftToFinalize.partnerName}</b> — {draftToFinalize.lines.length} dòng. Tổng sau chiết khấu:{' '}
            <b>
              {calculateDiscountedAmount(
                draftToFinalize.lines.reduce(
                  (s, l) => s + (Number(l.quantity || 0) / 1000) * Number(l.unitPricePerKg || 0),
                  0
                ),
                draftToFinalize.discountRate
              ).toLocaleString('vi-VN')}
            </b>{' '}
            đ
          </p>
        ) : null}
        <Form form={finalizeForm} layout="vertical" preserve={false}>
          <Form.Item
            name="date"
            label="Ngày giao dịch"
            rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
            Ghi nhận xuất kho tổng — không cần chọn cơ sở; trên phiếu/PDF hiển thị <b>Kho tổng</b>.
          </p>
        </Form>
      </Modal>
    </>
  );
};

export default PartnerCoffeeManagementSection;
